import React, { useCallback, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { rgba } from 'polished'
import { colors, timings } from 'shared-ui'
import Loader from './Loader'
import { NavLink } from 'react-router'

const cc = (obj) => {
    const arr = []
    for (const [key, value] of Object.entries(obj)) {
        if (!!value) arr.push(key)
    }
    return arr.join(' ')
}

const style = {
    table: (clickable, light) => css`
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 0.75em;
        font-size: 0.875em;

        td, th {
            vertical-align: middle;
        }

        tbody td > a, tbody td > div {
            color: unset;
            padding: 1em 1.125em;
            display: flex !important; // override serialized styles from emotion react
            align-items: center; 
            height: 100%;
        }

        tbody tr {
            transition: all ${timings.fast} ease;
            box-shadow: 0 0.25em 0.75em 0 rgba(0,0,0,0.3);
            ${clickable && !light ? `
                opacity: 0.8;
                background-color: ${colors.stuff};
            ` : ''}

            ${clickable ? `
                cursor: pointer;
                &:hover {
                    transition: all ${timings.snappy} ease;
                    opacity: 1;
                    box-shadow: 0 0.75em 1.5em 0 ${rgba(colors.black, 0.5)};
                    transform: translateY(-0.125em);
                }
            ` : ''}
        }
        tbody tr td {
            padding: 0;
            color: ${colors.white};
            overflow: hidden;
            margin: 0;
            height: 6em;
            position: relative;
            border-right: 1px solid ${rgba(colors.white, 0.1)};
            background-color: ${colors.solid};
            &:first-of-type {
                border-top-left-radius: 0.25rem;
                border-bottom-left-radius: 0.25rem;
            }
            &:last-of-type {
                border-top-right-radius: 0.25rem;
                border-bottom-right-radius: 0.25rem;
                border-right: none;
            }

            // luckily tables handle these beautifully
            &.wide {
                width: 100%;
            }
            &.shrink {
                width: 1px;
                text-align: center;
            }
        }
    `,
    headItem: (shrink, sortable, isSorting, isAsc) => css`
        padding: 1em 1.125em 0.2em 1.125em;
        transition: all ${timings.fast} ease;
        color: ${colors.white};
        opacity: 0.35;
        white-space: nowrap;

        .arrow {
            display: none;
            font-size: 0.75em;
            position: relative;
            top: 0.125em;
            transition: transform ${timings.smooth} ease;
        }
        ${shrink && `
            text-align: center;
            padding-left: 0.75em;
            padding-right: 0.75em;
        `}
        ${sortable && `
            cursor: pointer;
            user-select: none;
            .arrow {
                margin-left: 0.5em;
                display: inline-block;
                opacity: 0.75;
            }

            &:hover {
                transition: all ${timings.snappy} ease;
                color: ${colors.main1};
                opacity: 1;
            }
            ${isSorting && `
                opacity: 1;
                .arrow {
                    opacity: 1;
                    transform: rotate(180deg);
                }
                ${isAsc && `
                    .arrow {
                        transform: none;
                    }
                `}
            `}
        `}
    `,
}

export default function Table({
    data,
    columns,
    rowKey,
    onRowClick,
    getRowHref,
    sortOn,
    sortDirection: initialSortDirection = 'asc',
    showHeader = true,
    stickyHeader = false,
    headerBg,
    headerStyle,
    loading = false,
    fallback,
    light = false,
    colgroup,  // using this overrides the wide/shrink column classes
    ...props
}) {

    const [sortKey, setSortKey] = useState(sortOn)
    const [sortDirection, setSortDirection] = useState(initialSortDirection)

    const handleSort = useCallback((sortColumn) => {
        if (sortKey === sortColumn) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(sortColumn)
            setSortDirection('asc')
        }
    })

    const rows = useMemo(() => {
        const isNullish = (num) => (num === undefined || num === null)
        if (isNullish(sortKey)) return data

        const getSortValue = (a, b) => {
            if (isNullish(a[sortKey])) return sortDirection === 'asc' ? -1 : 1
            if (isNullish(b[sortKey])) return sortDirection === 'asc' ? 1 : -1

            if (typeof a[sortKey] === 'string' && typeof b[sortKey] === 'string') {
                return sortDirection === 'asc' ?
                    a[sortKey].localeCompare(b[sortKey])
                    : b[sortKey].localeCompare(a[sortKey])
            } else {
                return sortDirection === 'asc' ?
                    (a[sortKey] < b[sortKey] ? -1 : 1)
                    :
                    (a[sortKey] > b[sortKey] ? -1 : 1)
            }
        }
        return data.toSorted((a, b) => getSortValue(a, b))
    }, [data, sortKey, sortDirection])

    const [hasData, clickable, navigate, useOnRowClick] = useMemo(() => {
        const hasData = rows.length > 0

        return [
            hasData,
            hasData && (typeof getRowHref === 'function' || typeof onRowClick === 'function'),
            typeof getRowHref === 'function',
            typeof onRowClick === 'function',
        ]
    }, [getRowHref, onRowClick, rows])

    return (
        <div {...props}>
            <table css={style.table(clickable, light)}>
                {colgroup || null}
                {showHeader &&
                    <thead>
                        <tr>
                            {columns.map((column, i) => {
                                const isSorting = sortKey === column.key
                                const isAsc = sortDirection === 'asc'
                                return (
                                    <th
                                        key={column.key || i}
                                        css={[
                                            style.headItem(
                                                colgroup ? false : (column.shrink),
                                                column.sortable,
                                                isSorting,
                                                isAsc
                                            ),
                                            stickyHeader ? css`
                                                position: sticky; 
                                                top: 0;
                                                z-index: 1; 
                                                background: ${headerBg};
                                            ` : css``,
                                            headerStyle || css``
                                        ]}
                                        onClick={column.sortable ? () => handleSort(column.key) : undefined}
                                    >
                                        <div css={css`
                                            display: flex;
                                            justify-content: ${column.shrink && !colgroup ? 'center' : 'start'};
                                            align-items: center;
                                        `}>
                                            <span style={{ whiteSpace: column.headerWrap ? 'normal' : 'nowrap' }}>{column.header}</span>
                                            <span className='arrow'>&#x25BE;</span>
                                        </div>
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                }
                <tbody>
                    {hasData && rows.map((row, i) => (
                        <tr key={rowKey(row) || i} onClick={useOnRowClick ? () => onRowClick(row) : undefined}>
                            {columns.map((column, i) => (
                                <td key={column.key || i} className={colgroup ? null : cc({ wide: column.wide, shrink: column.shrink })}>
                                    {navigate ?
                                        <NavLink to={getRowHref?.(row)}>
                                            {column.renderData ? column.renderData(row) : row[column.key]}
                                        </NavLink>
                                        :
                                        <div>
                                            {column.renderData ? column.renderData(row) : row[column.key]}
                                        </div>
                                    }
                                </td>
                            ))}
                        </tr>
                    ))}
                    {(!loading && !hasData && fallback) &&
                        <tr>
                            {columns.map((column, i) => (
                                <td key={column.key || i} className={colgroup ? null : cc({ wide: column.wide, shrink: column.shrink })}>
                                    {i === 0 &&
                                        <div>
                                            {fallback}
                                        </div>
                                    }
                                </td>
                            ))}
                        </tr>
                    }
                    {loading &&
                        <tr>
                            {columns.map((column, i) => (
                                <td key={column.key || i} className={colgroup ? null : cc({ wide: column.wide, shrink: column.shrink })}>
                                    {i === 0 &&
                                        <div>
                                            <Loader size='1.5em' />
                                        </div>
                                    }
                                </td>
                            ))}
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    )
}
